import { useState, useEffect } from "react";

import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

export const useImpersonation = () => {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [targetEmail, setTargetEmail] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    // Check if returning from impersonation (via query param)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('impersonation_ended') === 'true') {
      // Force clear any lingering state
      sessionStorage.removeItem('impersonation_active');
      sessionStorage.removeItem('impersonation_target');
      sessionStorage.removeItem('admin_recovery_link');
      
      // Clean up URL
      urlParams.delete('impersonation_ended');
      const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, '', newUrl);
      
      setIsImpersonating(false);
      setTargetEmail(null);
      return;
    }
    
    // Check if we're in impersonation mode
    const impersonationActive = sessionStorage.getItem('impersonation_active') === 'true';
    const target = sessionStorage.getItem('impersonation_target');
    
    setIsImpersonating(impersonationActive);
    setTargetEmail(target);
  }, []);

  const endImpersonation = () => {
    try {
      // Clear React state immediately
      setIsImpersonating(false);
      setTargetEmail(null);

      const recoveryLink = sessionStorage.getItem('admin_recovery_link');

      // Clear impersonation state to prevent stuck UI
      sessionStorage.removeItem('impersonation_active');
      sessionStorage.removeItem('impersonation_target');
      sessionStorage.removeItem('admin_recovery_link');

      if (!recoveryLink) {
        toast({
          title: "Session expired",
          description: "Your admin session could not be restored. Please log in again.",
          variant: "destructive",
        });
        navigate('/auth');
        return;
      }

      // Navigate to the admin recovery magic link to restore the original session
      window.location.href = recoveryLink;
    } catch (error: any) {
      console.error('Error ending impersonation:', error);

      toast({
        title: "Error ending impersonation",
        description: error?.message || 'Please log in again.',
        variant: "destructive",
      });

      navigate('/auth');
    }
  };

  return {
    isImpersonating,
    targetEmail,
    endImpersonation,
  };
};
